import { useRef, useEffect, useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { MapPin, Globe } from "lucide-react";

export default function GlobalMap() {
    const { t } = useLanguage();
    const sectionRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) setIsVisible(true);
            },
            { threshold: 0.15 }
        );
        const current = sectionRef.current;
        if (current) observer.observe(current);
        return () => {
            if (current) observer.unobserve(current);
        };
    }, []);

    return (
        <section className="relative py-20 md:py-32 px-6 overflow-hidden" ref={sectionRef} data-testid="map-section">
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] to-[#0f2442]" />
            <div className="absolute inset-0 opacity-10">
                <svg viewBox="0 0 1200 600" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
                    <g fill="currentColor" className="text-blue-400">
                        {/* North America */}
                        {[[200,150],[220,140],[240,150],[210,170],[230,180],[250,170],[190,160],[260,160],[240,130],[220,160],[200,180],[230,150]].map(([x,y],i) => (
                            <circle key={`na${i}`} cx={x} cy={y} r="3" opacity="0.6" />
                        ))}
                        {/* South America */}
                        {[[280,300],[270,320],[290,340],[275,360],[285,380],[270,280],[295,310],[280,350],[265,340],[290,370]].map(([x,y],i) => (
                            <circle key={`sa${i}`} cx={x} cy={y} r="3" opacity="0.6" />
                        ))}
                        {/* Europe */}
                        {[[530,130],[550,120],[540,140],[560,150],[520,150],[510,140],[570,130],[535,160],[555,155],[545,125],[565,140],[525,135]].map(([x,y],i) => (
                            <circle key={`eu${i}`} cx={x} cy={y} r="3" opacity="0.6" />
                        ))}
                        {/* Africa */}
                        {[[540,250],[555,270],[530,290],[550,310],[540,330],[560,290],[525,270],[545,350],[535,240],[555,250]].map(([x,y],i) => (
                            <circle key={`af${i}`} cx={x} cy={y} r="3" opacity="0.6" />
                        ))}
                        {/* Asia */}
                        {[[700,140],[720,130],[740,150],[760,140],[710,160],[730,170],[750,165],[690,150],[770,155],[725,145],[680,170],[745,180]].map(([x,y],i) => (
                            <circle key={`as${i}`} cx={x} cy={y} r="3" opacity="0.6" />
                        ))}
                        {/* Russia */}
                        {[[650,100],[680,90],[710,95],[740,100],[660,110],[690,105],[720,110]].map(([x,y],i) => (
                            <circle key={`ru${i}`} cx={x} cy={y} r="3" opacity="0.6" />
                        ))}
                        {/* Oceania */}
                        {[[850,350],[870,340],[860,360],[880,350]].map(([x,y],i) => (
                            <circle key={`oc${i}`} cx={x} cy={y} r="3" opacity="0.6" />
                        ))}
                    </g>
                    <g stroke="currentColor" className="text-blue-500" strokeWidth="0.5" opacity="0.2">
                        <line x1="240" y1="150" x2="540" y2="140" />
                        <line x1="540" y1="140" x2="720" y2="140" />
                        <line x1="280" y1="300" x2="540" y2="250" />
                        <line x1="540" y1="140" x2="540" y2="250" />
                        <line x1="720" y1="140" x2="860" y2="350" />
                    </g>
                </svg>
            </div>

            <div
                className="relative z-10 max-w-7xl mx-auto"
                style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? "translateY(0)" : "translateY(24px)",
                    transition: "opacity 0.7s ease, transform 0.7s ease",
                }}
            >
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-300 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                        <Globe className="w-4 h-4" />
                        {t.map.title}
                    </div>
                    <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white">
                        {t.map.title}
                    </h2>
                    <p className="mt-4 text-base md:text-lg text-slate-400 max-w-2xl mx-auto">
                        {t.map.subtitle}
                    </p>
                </div>

                <div className="flex flex-wrap justify-center gap-3 md:gap-4">
                    {t.map.regions.map((region, i) => (
                        <div
                            key={region}
                            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/10 hover:border-white/20 rounded-full px-5 py-2.5 transition-all duration-300 cursor-default"
                            data-testid="map-region"
                            style={{
                                opacity: isVisible ? 1 : 0,
                                transform: isVisible ? "translateY(0)" : "translateY(16px)",
                                transition: `opacity 0.5s ease ${0.3 + i * 0.1}s, transform 0.5s ease ${0.3 + i * 0.1}s`,
                            }}
                        >
                            <MapPin className="w-4 h-4 text-blue-400" />
                            <span className="text-sm font-medium text-white">{region}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
